<template>
  <error-boundary module="form-module">
    <hr-module :key="slug" class="form-module" mobile-menu no-header>
      <template v-slot:left>
        <hr-steps></hr-steps>
      </template>

      <hr-module-header :title="state.currentDocument.title" sub-module>
        <p v-html="state.currentDocument.description"></p>
      </hr-module-header>

      <div v-if="isAnySignatureLoading" class="signature-loading-overlay">
        <hr-loader-block />
      </div>

      <hr-form
        v-if="state.currentDocument.formId"
        :document="state.currentDocument"
        @update="loadCurrentDocument" />

      <template v-else>
        <div v-show="!isAnySignatureLoading">
          <template
            v-for="(field, index) in state.currentDocument.fields"
            :key="index"
          >
            <hr-field-upload
              v-if="field.type === 'FILE_UPLOAD'"
              :key="`${state.currentDocument.name}-upload-${index}`"
              :error-message="field.fieldError"
              :model-value="state.currentDocument.fields[index]"
              class="document-module__field __upload"
              @update="(val) => updateUpload(index, val)"
            />
            <hr-field-signature
              v-else-if="field.type === 'DOCSIGNATURE_REQUEST'"
              :key="`${state.currentDocument.name}-signature-${index}`"
              v-model="state.currentDocument.fields[index]"
              class="document-module__field"
              @input="(val) => fieldChange(index, val)"
              @loading-start="handleSignatureLoadingStatus(index, true)"
              @loading-end="handleSignatureLoadingStatus(index, false)"
            />
            <hr-question-factory
              v-else-if="field.type === 'CUSTOM_QUESTION'"
              v-model="state.currentDocument.fields[index]"
              class="document-module__field"
              :key="`${state.currentDocument.name}-question-${index}`"
              @update:model-value="(val) => fieldChange(index, val)"
              :data-test-id="`hr-${state.currentDocument.name.toLowerCase()}-${index}`"
            />
          </template>

          <hr-button-save
            :state="state.saveState"
            :isDisabled="state.isSaveDisabled"
            class="col-xs-6 col-sm-12"
            @click="save"
          />
        </div>
      </template>

      <template v-slot:footer> </template
    ></hr-module>
  </error-boundary>
</template>

<script>
import { reactive, computed } from 'vue';
import store from '@/store';
import HrModuleHeader from '@/components/hr-module-header.vue';
import HrModule from '@/components/hr-module';
import HrSteps from '@/components/hr-steps.vue';
import HrForm from '@/components/hr-form.vue';
import HrFieldUpload from '@/components/hr-field-upload';
import HrFieldSignature from '@/components/hr-field-signature';
import HrQuestionFactory from '@/components/hr-question-factory.vue';
import HrButtonSave from '@/components/hr-button-save.vue';
import HrLoaderBlock from '@/components/hr-loader-block.vue';
import router from '@/router';
import cloneObj from '@/core/cloneObj';
import { documentCompletion } from '@/store/composables/document-completion';
import { DOCUMENT_STATUSES } from '@/modules/definitions';
import { DOCUMENT_NAMES } from '@/core/field-names';
import ErrorBoundary from '@/components/error-boundary.vue';
import { HR_ONBOARDING_STATES } from '../core/onboarding-states';

export default {
  name: 'FormModule',
  components: {
    ErrorBoundary,
    HrModule,
    HrSteps,
    HrModuleHeader,
    HrForm,
    HrFieldSignature,
    HrFieldUpload,
    HrQuestionFactory,
    HrButtonSave,
    HrLoaderBlock,
  },
  props: {
    document: {
      type: String,
      default: null,
    },
    slug: {
      type: String,
      default: null,
    },
  },
  setup() {
    const state = reactive({
      currentDocument: {},
      saveState: 'disabled',
      isSaveDisabled: true,
      showReviewButton: false,
      errorMessage: null,
      hasError: false,
      errorMessageArray: [],
      fieldValidation: {},
      signatureLoadingStates: new Map(),
      hasBackendError: computed(() => store.state.documentErrors?.length > 0),

      router: router,
    });

    const isAnySignatureLoading = computed(() => {
      return Array.from(state.signatureLoadingStates.values()).some(Boolean);
    });

    function loadCurrentDocument() {
      state.currentDocument = cloneObj(
        store.getters.documents.find(
          (doc) => doc.slug === state.router.currentRoute.params.slug
        ) || {}
      );
    }

    function menuToggle() {
      return store.commit('menuToggle');
    }

    async function save() {
      state.currentDocument.status =
        state.currentDocument.completion < 100
          ? DOCUMENT_STATUSES.DOCUMENT_IN_PROGRESS
          : DOCUMENT_STATUSES.DOCUMENT_SUBMITTED;

      await store.dispatch('saveForm', state.currentDocument);

      if (store.getters.onboardingStatus !== HR_ONBOARDING_STATES.STARTED) {
        await store.dispatch(
          'updateOnboardingStatus',
          HR_ONBOARDING_STATES.STARTED
        );
      }

      loadCurrentDocument();

      if (state.currentDocument.completion === 100) {
        menuToggle();
      }
    }

    async function updateUpload(index, val) {
      let fileUploadToBeSaved;
      if (val.requiredValues === undefined) {
        fileUploadToBeSaved = {
          ...val,
          fileValue: {
            ...val.fileValue,
          },
          value: {
            ...val.fileValue,
          },
        };
      } else {
        fileUploadToBeSaved = {
          ...val,
          fileValue: {
            ...val.fileValue,
            expiredAt:
              val.fileValue?.expiredAt &&
              new Date(val.fileValue.expiredAt)?.toISOString(),
          },
          value: {
            ...val.fileValue,
            expiredAt:
              val.fileValue.expiredAt &&
              new Date(val.fileValue.expiredAt)?.toISOString(),
          },
        };
      }

      await fieldChange(index, fileUploadToBeSaved);
    }

    async function saveSignature(val) {
      const {
        id: fieldId,
        docSignatureEmbedValue: {
          providerData: { signed: signedString },
        },
      } = val;

      state.currentDocument.status =
        state.currentDocument.completion < 100
          ? DOCUMENT_STATUSES.DOCUMENT_IN_PROGRESS
          : DOCUMENT_STATUSES.DOCUMENT_SUBMITTED;

      await store.dispatch('saveSignature', {
        fieldId,
        signed: signedString === 'true',
        documentId: state.currentDocument.id,
        currentDocument: state.currentDocument,
      });
      loadCurrentDocument();

      if (state.currentDocument.completion === 100) {
        menuToggle();
      }
    }

    async function fieldChange(index, val) {
      state.currentDocument.fields.splice(index, 1, val);

      const result = documentCompletion(state.currentDocument);
      if (result.completion < 100) {
        state.isSaveDisabled = true;
      } else {
        state.isSaveDisabled = false;
      }

      if (
        state.currentDocument.name === DOCUMENT_NAMES.DOC_SIGNATURE_REQUESTS
      ) {
        await saveSignature(val);
      }

      if (state.currentDocument.name === DOCUMENT_NAMES.FILE_UPLOADS) {
        save();
      }
    }

    function handleSignatureLoadingStatus(fieldIndex, status) {
      state.signatureLoadingStates.set(fieldIndex, status);
    }

    async function initialize() {
      loadCurrentDocument();
      state.isSaveDisabled = state.currentDocument?.completion < 100;
    }

    initialize();

    return {
      state,
      updateUpload,
      fieldChange,
      save,
      loadCurrentDocument,
      isAnySignatureLoading,
      handleSignatureLoadingStatus,
    };
  },
};
</script>

<style scoped>
.document-module__field {
  margin-bottom: 32px;
}

.signature-loading-overlay {
  position: relative;
  height: 200px;
  margin-bottom: 32px;
}
</style>
